import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Empty, Image, Row, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';
import type { ReportDocument, ReportRow, ReportSection, ReportTableSection, ReportTile } from '@/services/reportDocuments.service';
import AgeingBar from './AgeingBar';
import { formatReportCell, reportColumnAlign } from './reportCellFormat';

const { Title, Text } = Typography;

const EDGE = new Set(['opening', 'subtotal', 'total']);

function rowStyle(r: ReportRow): React.CSSProperties | undefined {
  if (r.style && EDGE.has(r.style)) return { background: '#E8EEF5', fontWeight: 600 };
  if (r.style === 'group') return { fontWeight: 600 };
  if (r.style === 'danger') return { background: '#FDECEA' };
  if (r.style === 'muted') return { color: '#8a94a6' };
  return undefined;
}

function TableSection({ section }: { section: ReportTableSection }) {
  const { t } = useTranslation('finance');
  const columns: ColumnsType<ReportRow & { _i: number }> = section.columns.map((c, idx) => ({
    title: c.label,
    key: c.key,
    align: reportColumnAlign(c),
    onCell: (r) => {
      const span = r.span ?? 1;
      return { style: rowStyle(r), colSpan: idx === 0 ? span : idx < span ? 0 : 1 };
    },
    render: (_: unknown, r) => {
      if (c.kind === 'image') {
        const src = typeof r.cells[c.key] === 'string' ? resolvePublicAssetUrl(r.cells[c.key] as string) : '';
        return src ? (
          <Image
            src={src}
            alt=""
            width={56}
            height={56}
            style={{ objectFit: 'contain', border: '1px solid #e5e9f0', borderRadius: 4, background: '#fff', padding: 2 }}
          />
        ) : null;
      }
      const text = formatReportCell(r.cells[c.key], idx === 0 && (r.span ?? 1) > 1 ? 'text' : c.kind);
      const link = r.links?.find((l) => l.column === c.key);
      const negative = c.kind === 'money' && typeof r.cells[c.key] === 'number' && (r.cells[c.key] as number) < -0.004;
      const content = link ? <Link to={link.path}>{text}</Link> : text;
      return (
        <span style={{ paddingLeft: idx === 0 ? (r.indent ?? 0) * 14 : 0, color: negative ? '#B42318' : undefined, whiteSpace: 'pre-line' }}>
          {content}
        </span>
      );
    },
  }));
  return (
    <Table
      size="small"
      rowKey="_i"
      columns={columns}
      dataSource={section.rows.map((r, i) => ({ ...r, _i: i }))}
      pagination={
        section.rows.length > 100
          ? { defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: [50, 100, 200, 500], showTotal: (count) => t('reportRowCount', { count }) }
          : false
      }
      locale={{ emptyText: <Empty description={section.emptyText ?? t('noRecordsSelectedCriteria')} /> }}
      scroll={{ x: 'max-content' }}
    />
  );
}

function SectionView({ section }: { section: ReportSection }) {
  const { t } = useTranslation('finance');
  const heading = section.kind === 'totals' ? t('totals') : section.heading || undefined;
  const subheading = section.kind === 'table' || section.kind === 'ageing' ? section.subheading : undefined;
  return (
    <Card size="small" style={{ marginBottom: 16 }} title={heading} extra={subheading ? <Text type="secondary">{subheading}</Text> : undefined}>
      {section.kind === 'table' && <TableSection section={section} />}
      {section.kind === 'ageing' && (
        <AgeingBar
          format={(n) => `${section.currency} ${formatReportCell(n, 'money')}`}
          buckets={section.buckets.map((b, i) => ({ key: `${i}`, label: b.label, amount: b.amount, overdue: b.overdue }))}
        />
      )}
      {section.kind === 'notes' && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {section.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
      {section.kind === 'key_values' &&
        section.rows.map(([k, v]) => (
          <div key={k}>
            <Text type="secondary">{k}: </Text>
            {v}
          </div>
        ))}
      {section.kind === 'totals' &&
        section.lines.map((l) => (
          <div key={l.label} style={{ display: 'flex', justifyContent: 'space-between', fontWeight: l.strong ? 600 : undefined }}>
            <span>{l.label}</span>
            <span>{`${section.currency} ${formatReportCell(l.amount, 'money')}`}</span>
          </div>
        ))}
      {section.kind === 'parties' && (
        <Row gutter={16}>
          {section.parties.map((p) => (
            <Col key={p.label} span={24 / Math.max(section.parties.length, 1)}>
              <Text type="secondary">{p.label}</Text>
              <div>
                <Text strong>{p.name}</Text>
              </div>
              {p.lines.map((l) => (
                <div key={l}>{l}</div>
              ))}
            </Col>
          ))}
        </Row>
      )}
      {section.kind === 'text' &&
        section.paragraphs.map((p, i) => (
          <p key={i} style={{ margin: '0 0 4px' }}>
            {p}
          </p>
        ))}
      {section.kind === 'signatures' &&
        section.signers.map((s, i) => (
          <div key={i}>
            <Text type="secondary">{s.role || t('approvedBy')}: </Text>
            {s.name}
            {s.designation ? ` (${s.designation})` : ''} {s.signed_at ? formatReportCell(s.signed_at, 'date') : s.mode === 'wet' ? '' : t('pending')}
            {s.signed_at && s.signature_image_url ? (
              <img
                src={resolvePublicAssetUrl(s.signature_image_url) || ''}
                alt={t('signatureOf', { name: s.name })}
                style={{ display: 'block', maxHeight: 36, maxWidth: 140, objectFit: 'contain', marginTop: 2 }}
              />
            ) : null}
          </div>
        ))}
    </Card>
  );
}

function tileText(t: ReportTile): string {
  const kind = t.kind ?? (typeof t.value === 'number' ? 'money' : 'text');
  return formatReportCell(t.value, kind);
}

/** Generic on-screen rendering of a server report document (same content as the PDF). */
const ReportDocumentView: React.FC<{ document: ReportDocument; hideTitle?: boolean }> = ({ document, hideTitle }) => {
  const { t } = useTranslation('finance');
  return (
    <div lang={document.language || 'en'} dir={document.dir || (document.language === 'ar' ? 'rtl' : 'ltr')}>
      {!hideTitle && (
        <div style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            {document.title}
            {document.party ? ` - ${document.party.name}` : ''}
          </Title>
          <Text type="secondary">
            {document.reference}
            {document.subtitle ? ` | ${document.subtitle}` : ''}
            {document.filters.length ? ` | ${document.filters.map(([k, v]) => `${k}: ${v}`).join(' | ')}` : ''}
          </Text>
        </div>
      )}
      {document.tiles.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {document.tiles.map((tile) => (
            <Col key={tile.label} xs={12} md={8} xl={Math.max(3, Math.floor(24 / Math.min(document.tiles.length, 6)))}>
              <Card
                size="small"
                style={{
                  background: tile.tone === 'strong' ? '#243B53' : tile.tone === 'danger' ? '#FDECEA' : tile.tone === 'success' ? '#E7F5EE' : undefined,
                }}
              >
                <Statistic
                  title={<span style={{ color: tile.tone === 'strong' ? '#C9D6E3' : undefined }}>{tile.label}</span>}
                  value={tileText(tile)}
                  prefix={tile.currency && typeof tile.value === 'number' ? <span style={{ fontSize: 13 }}>{tile.currency}</span> : undefined}
                  valueStyle={{
                    fontSize: 18,
                    color: tile.tone === 'strong' ? '#fff' : tile.tone === 'danger' ? '#B42318' : tile.tone === 'success' ? '#1E7B4F' : undefined,
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
      {document.sections.map((s, i) => (
        <SectionView key={`${s.kind}-${i}`} section={s} />
      ))}
      {document.notes.length > 0 && (
        <Card size="small" title={t('notes')}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {document.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default ReportDocumentView;
